/**
 * The virtualised grid (spec M1-B4 §6, §8.1, §8.3, §8.4).
 *
 * One scroll container owns both axes. Rows come from one flat list — `[group header, rows…,
 * add-item row, footer]` per group — so the total height is exact and no measurement pass is
 * needed (§6). Columns are virtualised from a prefix sum, and the sticky region is laid out by
 * `position: sticky; left: 0` on every row's left half, which is what keeps the group headers,
 * the floating header and the footers pixel-aligned with the body during a horizontal scroll
 * without a single extra listener (L3).
 */
import type { ColumnId, GroupId, ItemId } from '@elizmonday/shared';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import type { CellRenderContext } from '../columns/types';
import type { BoardGroupView } from '../data/types';
import { AddItemRow } from './AddItemRow';
import { useTable, useTableContext } from './context';
import { deriveGrid, groupAtOffset, groupsToPage } from './derive';
import { GridRow } from './GridRow';
import { GroupFooter } from './GroupFooter';
import { GroupHeader } from './GroupHeader';
import {
  GROUP_COLLAPSED_STRIP_HEIGHT,
  GROUP_HEADER_HEIGHT,
  ROW_OVERSCAN,
  rowExtent,
  NARROW_GRID_WIDTH,
  nameColumnWidth,
  stickyWidth as stickyWidthOf,
  visibleColumnRange,
} from './layout';
import {
  AddSubitemRow,
  SubitemRow,
  SubitemsHeaderRow,
  SubitemsUnavailableRow,
} from './SubitemsTable';
import {
  ROW_DRAG_TYPE,
  draggedItemIds,
  dropOnGroupEnd,
  dropOnGroupHeader,
  dropOnItem,
  edgeOf,
  isNoOpDrop,
  isNoOpDropForMany,
  rowSlots,
  slotOf,
  targetForMany,
  type RowDropHover,
  type RowSlot,
} from './row-drag';
import type { FilterGroup } from './settings';
import {
  EMPTY_ITEM_SELECTION,
  extendItemRange,
  extendTo,
  focusCell,
  moveFocus,
  escape as escapeSelection,
  selectGroup,
  selectItems,
  tab as tabTo,
  toggleItem,
  toggleItemKeepAnchor,
  type CellAddress,
} from './selection';

/**
 * **P-302 — whose paste is this?**
 *
 * The scroller carries the grid's clipboard surface (`onPaste` below), and a `paste` bubbles up
 * to it from **everything** inside it: the `+ Add item` draft input, `+ Add subitem`, an inline
 * cell editor, the search box of a status/dropdown picker. The handler cancelled every one of
 * them (`preventDefault` before the range paste), so `Ctrl/Cmd+V` into the open `+ Add item`
 * input left it empty — Nico's "copy>paste when I create a new task is not working" — and the
 * same went for every other native input in the grid.
 *
 * The rule is the browser's own: an editable element owns its paste. The grid takes the event
 * only when it is aimed at **its** surface — the scroller itself, or a cell, which is a `div`
 * and not editable — so a range paste over a selection still works exactly as it did.
 */
export function pasteBelongsToGrid(target: EventTarget | null, surface: Element | null): boolean {
  if (!(target instanceof Element)) return true;
  const editable = target.closest(
    'input, textarea, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]',
  );
  if (editable === null || editable === surface) return true;
  /*
   * The grid's **own** editing surfaces keep their paste: the inline editor inside a cell
   * (P-277 — a paste while editing replaces the value and commits) and the popover editors,
   * which portal to the body but bubble through the React tree (P-282). Every other text box
   * inside the scroller — "+ Add item", a picker's search field — is the browser's (P-302).
   */
  return editable.closest('[data-testid^="cell-"], [data-editing], .ez-anchored-popover') !== null;
}

export interface GridProps {
  /** P-266: `options.tab` is the item-card tab the gesture asks for (the bubble: Updates). */
  onOpenItem(itemId: ItemId, options?: { tab?: 'updates' }): void;
}

export function Grid({ onOpenItem }: GridProps) {
  const { store, source } = useTableContext();
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const extraRows = useTable((state) => state.extraRows);
  const selection = useTable((state) => state.selection);
  const itemSelection = useTable((state) => state.itemSelection);
  const editing = useTable((state) => state.editing);
  const cursors = useTable((state) => state.cursors);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  /**
   * `scrollLeft` is a **ref**, not state: a horizontal scroll must not re-render 25 rows x 13
   * cells per event (P2's 60 fps). What React needs is only the *column range*, and that
   * changes once every ~140 px; the floating header's `translateX` is written imperatively.
   */
  const scrollLeftRef = useRef(0);
  const [columnRange, setColumnRange] = useState({ start: 0, end: 0 });
  /** The row the pointer is over during a row drag, and where the line is painted. */
  const [rowDrop, setRowDrop] = useState<RowDropHover | null>(null);
  /**
   * P-106 — the keyboard equivalent of the row drag: `Space` picks the focused row up, the
   * arrows move it between the slots `rowSlots` lists, `Space` drops it and `Escape` puts it
   * back. The pointer path is untouched; this is additive.
   */
  const [keyboardDrag, setKeyboardDrag] = useState<{
    itemId: ItemId;
    slots: readonly RowSlot[];
    at: number;
  } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState({ width: 1089, height: 812 });

  const expandedItemIds = useTable((state) => state.expandedItemIds);
  const subitemsByParent = useTable((state) => state.subitemsByParent);
  const subitemColumns = useTable((state) => state.subitemColumns);
  /** P-308: the parents whose subitems read was refused, and the api's own sentence. */
  const subitemsUnavailableFor = useTable((state) => state.subitemsUnavailableFor);
  const subitemsError = useTable((state) => state.subitemsError);
  /** P-090: set while `window.print()` runs — every row is mounted for the print dialog. */
  const printing = useTable((state) => state.printing);

  /**
   * §8.13's one condition, measured once and read by everything: the **grid's** width, not the
   * window's. It decides what stays in the sticky region (`deriveGrid`, `stickyWidth`), how
   * wide the name cell may be (`nameColumnWidth`) and what the stylesheet paints
   * (`data-narrow`). A board is narrow on a phone *and* when the activity log reserves its
   * 700 px of a 1440 px window (P-016, round 8f).
   */
  const narrow = viewport.width < NARROW_GRID_WIDTH;

  const grid = useMemo(
    () =>
      deriveGrid(
        payload,
        effective,
        extraRows,
        true,
        {
          expandedItemIds,
          byParent: subitemsByParent,
          columns: subitemColumns,
          unavailableFor: subitemsUnavailableFor,
          error: subitemsError,
        },
        { narrow },
      ),
    [
      payload,
      effective,
      extraRows,
      expandedItemIds,
      subitemsByParent,
      subitemColumns,
      subitemsUnavailableFor,
      subitemsError,
      narrow,
    ],
  );

  /**
   * M0-F9 §5 (F9-D5): on a phone the name **cell** (`grid.nameWidth`, already stripped of the
   * sticky chrome by P-021) is clamped so one data column stays visible; at every other width
   * this is `grid.nameWidth` unchanged. Every consumer
   * below takes it from here, so the rows, the group headers and the column-header strip
   * cannot disagree about where the scrolling half starts.
   */
  const nameWidth = nameColumnWidth(grid.nameWidth, viewport.width);

  const stickyWidth = stickyWidthOf(
    nameWidth,
    grid.pinned
      .filter((column) => column.id !== 'name')
      .reduce((total, column) => total + (column.width ?? 140), 0),
    viewport.width,
  );

  const virtualizer = useVirtualizer({
    count: grid.rows.length,
    getScrollElement: () => scrollerRef.current,
    // `rowExtent`, not `height`: an expanded parent's nested header and add-subitem rows carry
    // the 16/30 px of air around the block, so the total height stays exact (M3.S1 §2.5).
    estimateSize: (index) => {
      const row = grid.rows[index];
      return row ? rowExtent(row) : grid.rowHeight;
    },
    /**
     * **P-090.** Printing a virtualised grid prints the ~19 rows that happen to be mounted.
     * While `printing` is set the overscan covers the whole board, so every row is in the DOM
     * for the (blocking) print dialog and the virtualiser goes back to normal straight after.
     */
    overscan: printing ? grid.rows.length : ROW_OVERSCAN,
  });

  /**
   * 6i / q455. TanStack Virtual keeps every row's size in its own cache and only calls
   * `estimateSize` for an index it has never seen — so a settings change that makes the rows
   * *taller* is invisible until something else forces a re-measure. Nothing had ever changed
   * the row height at runtime before (`itemHeight`'s medium/large are accepted and ignored,
   * Q173), so this never showed: `Wrap text` set `data-wrap` on the cells and left every row
   * 36 px tall. Re-measuring when the derived height changes is the whole fix.
   */
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [virtualizer, grid.rowHeight]);

  useLayoutEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;
    const measure = () => setViewport({ width: node.clientWidth, height: node.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const syncColumns = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;
    scrollLeftRef.current = node.scrollLeft;
    if (floatingRef.current) {
      floatingRef.current.style.transform = `translateX(${-node.scrollLeft}px)`;
    }
    const next = visibleColumnRange(
      grid.layout,
      Math.max(0, node.scrollLeft - stickyWidth),
      node.clientWidth,
    );
    setColumnRange((current) => (current.start === next.start && current.end === next.end ? current : next));
  }, [grid.layout, stickyWidth]);

  const onScroll = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;
    syncColumns();
    setScrollTop((current) => (Math.abs(current - node.scrollTop) < 1 ? current : node.scrollTop));
  }, [syncColumns]);

  // The range has to be right before the first paint and after every layout change.
  useLayoutEffect(syncColumns, [syncColumns, viewport.width]);

  /* ── infinite paging (§5.2, P7) ─────────────────────────────────────────── */

  useEffect(() => {
    const last = grid.rows[grid.rows.length - 1];
    if (!last) return;
    const bottom = scrollTop + viewport.height;
    // The group the viewport's bottom is inside, and (P-328) every group whose loaded end is
    // within one viewport of the visible band. `loadMore` joins a page already running.
    for (const groupId of groupsToPage(grid, scrollTop, bottom, cursors, viewport.height)) {
      void store.getState().loadMore(groupId);
    }
  }, [grid, scrollTop, viewport.height, cursors, store]);

  /* ── keyboard (§8.3) ────────────────────────────────────────────────────── */

  const context: CellRenderContext = useMemo(
    () => ({
      people: payload?.people ?? [],
      // P-122: the account tag vocabulary `tags` cells and their picker read. Without it the
      // picker was empty on every board and a populated cell rendered `#4711` chips.
      tags: payload?.tags ?? [],
      // P-122: `create_or_get_tag`, which the picker's `Create "…"` row asks for. The board
      // screen owns the source; the type folder only calls this.
      ...(source.createTag && payload
        ? {
            createTag: async (name: string) => {
              const tag = await source.createTag!({ boardId: payload.board.id, name });
              return tag;
            },
          }
        : {}),
      // `progress` and `formula` are computed from sibling columns (M2 tier-2 §5.15, §5.20).
      columns: payload?.columns ?? [],
      search: effective.search,
      timezone: 'UTC',
    }),
    [payload, payload?.people, payload?.tags, payload?.columns, effective.search, source],
  );
  /*
   * P-300 · a subitem row's cells are computed against the **subitems board's** columns: a
   * formula there names its own siblings, and with the parent's list it answered "a column
   * named … doesn't exist" for every subitem the api had not materialised.
   */
  const subitemContext: CellRenderContext = useMemo(
    () => ({ ...context, columns: subitemColumns }),
    [context, subitemColumns],
  );

  const scrollIntoView = useCallback(
    (address: CellAddress) => {
      const index = grid.rows.findIndex((row) => row.kind === 'row' && row.itemId === address.itemId);
      if (index >= 0) virtualizer.scrollToIndex(index, { align: 'auto' });
      const box = grid.layout.find((entry) => entry.id === address.columnId);
      const node = scrollerRef.current;
      if (box && node) {
        const left = box.left;
        const visibleLeft = node.scrollLeft;
        const visibleRight = visibleLeft + node.clientWidth - stickyWidth;
        if (left < visibleLeft) node.scrollLeft = left;
        else if (left + box.width > visibleRight)
          node.scrollLeft = left + box.width - node.clientWidth + stickyWidth;
      }
    },
    [grid, virtualizer, stickyWidth],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const state = store.getState();
      const geometry = grid.geometry;
      const current = state.selection;
      const meta = event.metaKey || event.ctrlKey;

      if (event.key === 'Escape') {
        event.preventDefault();
        /*
         * **P-109 / `[B4§A4]`.** The focus ring is application state; DOM focus is not. The
         * editor unmounts its own input on Escape, so without this the focus falls to
         * `<body>` — the grid still *looks* focused and the very next key is lost, which is
         * exactly why an `F2` pressed straight after an `Escape` opened nothing. The roving
         * focus model `[B4§A1]` puts the tab stop on the scroller, so that is where it goes
         * back. It is taken **before** the editor unmounts, so nothing can race it.
         */
        scrollerRef.current?.focus({ preventScroll: true });
        if (keyboardDrag) {
          // A keyboard drag in flight is cancelled and the row stays where it was.
          setKeyboardDrag(null);
          setRowDrop(null);
          setAnnouncement('Move cancelled');
          return;
        }
        state.stopEditing();
        state.setSelection(escapeSelection(current));
        return;
      }

      if (state.editing) return; // the editor owns every other key while it is open

      /* ── the keyboard row drag (P-106, `[B4§A1]`) ─────────────────────────
       *
       * `Space` on the focused row's name cell picks it up, the arrows walk it through the
       * positions `rowSlots` lists — the top of every group and after every other row, so the
       * group boundary is crossed by carrying on — and `Space` drops it through the very same
       * `store.reorderItem` the pointer drop calls. `Escape` (above) puts it back.
       */
      if (keyboardDrag && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const at = Math.max(0, Math.min(keyboardDrag.slots.length - 1, keyboardDrag.at + delta));
        const slot = keyboardDrag.slots[at];
        setKeyboardDrag({ ...keyboardDrag, at });
        setRowDrop(slot ? { rowKey: slot.rowKey, edge: slot.edge, target: slot.target } : null);
        if (slot) setAnnouncement(`Position ${slot.index} of ${slot.of}`);
        return;
      }
      if (event.key === ' ') {
        if (keyboardDrag) {
          event.preventDefault();
          const slot = keyboardDrag.slots[keyboardDrag.at];
          const itemId = keyboardDrag.itemId;
          setKeyboardDrag(null);
          setRowDrop(null);
          if (slot && !isNoOpDrop(grid.rows, itemId, slot.target)) {
            void state.reorderItem(itemId, slot.target.groupId, slot.target.afterItemId);
            setAnnouncement(`Moved to position ${slot.index} of ${slot.of}`);
          } else setAnnouncement('Move cancelled');
          return;
        }
        const handle = (event.target as HTMLElement | null)?.closest<HTMLElement>(
          '.ez-row__name[draggable="true"]',
        );
        const itemId = handle?.closest<HTMLElement>('[data-item-id]')?.dataset.itemId as
          | ItemId
          | undefined;
        // F6: no reorder while a sort is active — the position written is not the order shown.
        if (itemId && state.effective.sort.length === 0) {
          event.preventDefault();
          const slots = rowSlots(grid.rows, itemId);
          const at = slotOf(slots, grid.rows, itemId);
          setKeyboardDrag({ itemId, slots, at });
          const slot = slots[at];
          setRowDrop(slot ? { rowKey: slot.rowKey, edge: slot.edge, target: slot.target } : null);
          setAnnouncement(
            slot
              ? `Picked up. Position ${slot.index} of ${slot.of}. Use the arrows, then Space to drop.`
              : 'Picked up',
          );
          return;
        }
      }

      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight': {
          event.preventDefault();
          const direction = event.key.replace('Arrow', '').toLowerCase() as 'up' | 'down' | 'left' | 'right';
          const next = moveFocus(geometry, current, direction, { extend: event.shiftKey, jump: meta });
          state.setSelection(next);
          if (next.focus) scrollIntoView(next.focus);
          return;
        }
        case 'Tab': {
          if (!current.focus) return;
          event.preventDefault();
          const next = tabTo(geometry, current.focus, event.shiftKey);
          if (next) {
            state.setSelection(focusCell(geometry, next));
            scrollIntoView(next);
          }
          return;
        }
        case 'Enter':
        case 'F2': {
          if (!current.focus) return;
          event.preventDefault();
          state.startEditing(current.focus);
          return;
        }
        case 'Delete':
        case 'Backspace': {
          event.preventDefault();
          void state.clearSelection();
          return;
        }
        case 'a': {
          if (!meta) break;
          event.preventDefault();
          state.setSelection(selectGroup(geometry, current));
          return;
        }
        case 'c': {
          if (!meta) break;
          event.preventDefault();
          void state.copySelection();
          return;
        }
        case 'x': {
          if (!meta) break;
          event.preventDefault();
          void state.copySelection().then(() => state.clearSelection());
          return;
        }
        case 'z': {
          if (!meta) break;
          event.preventDefault();
          // P-105: `Ctrl/Cmd+Shift+Z` is the second redo binding, beside `Ctrl/Cmd+Y`.
          if (event.shiftKey) void state.redoLast();
          else void state.undoLast();
          return;
        }
        case 'y': {
          if (!meta) break;
          event.preventDefault();
          void state.redoLast();
          return;
        }
        default:
          break;
      }

      // Typing a printable character opens the editor seeded with it (§8.3, ASSUMED Q51).
      if (!meta && !event.altKey && event.key.length === 1 && current.focus) {
        event.preventDefault();
        state.startEditing(current.focus, event.key);
      }
    },
    [grid.geometry, grid.rows, keyboardDrag, scrollIntoView, store],
  );

  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      // P-302: a paste aimed at a native input inside the scroller is that input's, not the grid's.
      if (!pasteBelongsToGrid(event.target, scrollerRef.current)) return;
      const text = event.clipboardData.getData('text/plain');
      if (!text) return;
      event.preventDefault();
      void store.getState().paste(text);
    },
    [store],
  );

  /* ── mouse ──────────────────────────────────────────────────────────────── */

  const onFocusCell = useCallback(
    (address: CellAddress, event: ReactMouseEvent) => {
      const state = store.getState();
      state.setSelection(
        event.shiftKey
          ? extendTo(grid.geometry, state.selection, address)
          : focusCell(grid.geometry, address),
      );
      state.stopEditing();
    },
    [grid.geometry, store],
  );

  /**
   * **P-109 / `[B4§A4]`.** Closing an editor without committing — `Escape`, an outside click —
   * hands the focus back to the grid. It has to happen **before** `stopEditing` unmounts the
   * editor's input, or the browser drops the focus to `<body>` and the next key is lost: that
   * is exactly why an `F2` pressed straight after an `Escape` opened nothing. The inline
   * editors stop the `Escape` from bubbling (`columns/primitives.tsx`), so the grid's own
   * `onKeyDown` never sees it and this is the only place that can.
   */
  const cancelEditing = useCallback(() => {
    scrollerRef.current?.focus({ preventScroll: true });
    store.getState().stopEditing();
  }, [store]);

  const toggleChecked = useCallback(
    (itemId: ItemId, event: ReactMouseEvent) => {
      const state = store.getState();
      const current = state.itemSelection;
      if (event.shiftKey) state.setItemSelection(extendItemRange(current, grid.itemOrder, itemId));
      else if (event.metaKey || event.ctrlKey) state.setItemSelection(toggleItemKeepAnchor(current, itemId));
      else state.setItemSelection(toggleItem(current, itemId));
    },
    [grid.itemOrder, store],
  );

  /* ── the floating group header (§8.1) ───────────────────────────────────── */

  const floatingGroup: BoardGroupView | null = useMemo(() => {
    if (!payload) return null;
    const at = groupAtOffset(grid, scrollTop + 1);
    if (!at) return null;
    const group = payload.groups.find((entry) => entry.id === at.groupId) ?? null;
    // The header only floats once its own header has scrolled out of the viewport.
    const headerIndex = grid.rows.findIndex(
      (row) => row.kind === 'group-header' && row.groupId === at.groupId,
    );
    if (headerIndex < 0) return null;
    const headerTop = grid.tops[headerIndex] ?? 0;
    return scrollTop > headerTop ? group : null;
  }, [payload, grid, scrollTop]);

  if (!payload) return null;

  const readOnlyColumnIds = payload.permissions.readOnlyColumnIds.map(String);
  /*
   * **P-266.** monday paints the row's `+` only on a board that *has* a subitems column — a
   * board without one shows no `+` at all — and only for an actor who may write items. Both
   * halves are answered here, once for the whole grid, so the row stays a pure renderer.
   * The `subtasks` column is never a grid column (P-023), which is why this reads
   * `payload.columns` rather than the laid-out ones.
   */
  const canAddSubitem =
    payload.permissions.canEditContent &&
    payload.columns.some((column) => column.type === 'subtasks');
  /** 6i / q455: `Wrap text` is a view setting, so it reaches the cells the same way. */
  const wrappedColumnIds = effective.wrappedColumnIds.map(String);
  const sortedBy = new Map(effective.sort.map((rule) => [rule.columnId as string, rule.direction]));
  /*
   * Row drag is refused while a sort is active (B4 §F6): the `position` a drop would write is
   * not the order on screen, so the row would jump somewhere else the moment it landed. The
   * name cell then carries the reason as its tooltip instead of silently doing nothing.
   */
  const rowDragEnabled = effective.sort.length === 0;
  const filteredColumnIds = new Set(collectFilterColumns(effective.filter));
  const state = store.getState();

  /*
   * The drag events are delegated on the canvas rather than bound per row: the grid is
   * virtualised, so binding three listeners to each of ~60 mounted rows (and rebinding them
   * on every scroll) costs more than one `closest()` per `dragover`. Each drop target marks
   * itself with `data-drop-row`, and this reads it.
   */
  const readDropTarget = (event: ReactDragEvent<HTMLElement>): RowDropHover | null => {
    const node = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-drop-row]');
    if (!node) return null;
    const box = node.getBoundingClientRect();
    const kind = node.dataset.dropRow;

    if (kind === 'item') {
      const itemId = node.dataset.itemId as ItemId | undefined;
      if (!itemId) return null;
      const edge = edgeOf(box, event.clientY);
      const target = dropOnItem(grid.rows, itemId, edge);
      return target ? { rowKey: `item:${itemId}`, edge, target } : null;
    }
    if (kind === 'group-header') {
      const groupId = node.dataset.group as GroupId | undefined;
      if (!groupId) return null;
      return { rowKey: `group:${groupId}`, edge: 'below', target: dropOnGroupHeader(groupId) };
    }
    if (kind === 'group-end') {
      const groupId = node.dataset.group as GroupId | undefined;
      if (!groupId) return null;
      return { rowKey: `end:${groupId}`, edge: 'above', target: dropOnGroupEnd(grid.rows, groupId) };
    }
    return null;
  };

  const onCanvasDragOver = (event: ReactDragEvent<HTMLDivElement>) => {
    if (!rowDragEnabled || !event.dataTransfer.types.includes(ROW_DRAG_TYPE)) return;
    const hover = readDropTarget(event);
    // Only `preventDefault` marks a valid drop zone; without it the browser refuses the drop.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setRowDrop((current) =>
      current?.rowKey === hover?.rowKey && current?.edge === hover?.edge ? current : hover,
    );
  };

  const onCanvasDrop = (event: ReactDragEvent<HTMLDivElement>) => {
    const payload = event.dataTransfer.getData(ROW_DRAG_TYPE);
    const hover = rowDrop ?? readDropTarget(event);
    setRowDrop(null);
    store.getState().setInteracting(false);
    if (!rowDragEnabled || !payload || !hover) return;
    event.preventDefault();
    /*
     * **P-103.** The payload is the whole selection when the row that was picked up is part
     * of it, and that one row otherwise — `GridRow` asks `dragPayload` for it at `dragstart`.
     * One drop of n rows is one mutation (`store.reorderItems`), so the activity log gets
     * monday's batch row rather than n single ones `[ba§2]`, and the rows keep their relative
     * order because the ids arrive in board order.
     */
    const itemIds = payload.split(',').filter(Boolean) as ItemId[];
    const first = itemIds[0];
    if (!first) return;
    if (itemIds.length === 1) {
      // A drop that would not move the row writes nothing: monday logs no such reorder (Q154).
      if (isNoOpDrop(grid.rows, first, hover.target)) return;
      void store.getState().reorderItem(first, hover.target.groupId, hover.target.afterItemId);
      return;
    }
    // The anchor may itself be one of the rows being moved; it walks back to one that is not.
    const target = targetForMany(grid.rows, itemIds, hover.target);
    if (isNoOpDropForMany(grid.rows, itemIds, target)) return;
    void store.getState().reorderItems(itemIds, target.groupId, target.afterItemId);
  };

  /** P-103 — what `GridRow`'s `dragstart` puts on the dataTransfer. Identity-stable. */
  const dragPayload = (itemId: ItemId): string =>
    draggedItemIds(grid.rows, itemId, store.getState().itemSelection.ids).join(',');

  /**
   * **P-102** — a group dropped on another group's header. `groups.move` takes the full
   * placement, so the drop is expressed relatively rather than as top/bottom, and the header
   * itself owns the gesture (`GroupHeader`, `text/ez-group`).
   */
  const onDropGroup = (movedGroupId: GroupId, overGroupId: GroupId, edge: 'above' | 'below') => {
    if (String(movedGroupId) === String(overGroupId)) return;
    void store.getState().moveGroup(movedGroupId, {
      method: edge === 'above' ? 'before_at' : 'after_at',
      relativeTo: String(overGroupId),
    });
  };

  const headerProps = (group: BoardGroupView, floating: boolean) => ({
    group,
    collapsed: group.collapsed,
    pinned: grid.pinned,
    scrolling: grid.scrolling,
    layout: grid.layout,
    columnRange,
    nameWidth,
    stickyWidth,
    scrollWidth: grid.scrollWidth,
    allSelected:
      (grid.rowsByGroup.get(group.id)?.length ?? 0) > 0 &&
      (grid.rowsByGroup.get(group.id) ?? []).every((row) => itemSelection.ids.includes(row.id)),
    sortedBy,
    filteredColumnIds,
    itemTerminology: payload.board.itemTerminology,
    itemTerminologyPlural: payload.board.itemTerminologyPlural,
    floating,
    onToggleCollapsed: () => void state.toggleGroupCollapsed(group.id, !group.collapsed),
    onRenameGroup: (title: string) => void state.renameGroup(group.id, title),
    onSelectAll: (checked: boolean) => {
      const ids = (grid.rowsByGroup.get(group.id) ?? []).map((row) => row.id);
      state.setItemSelection(
        checked
          ? selectItems(grid.itemOrder, [...new Set([...itemSelection.ids, ...ids])])
          : selectItems(
              grid.itemOrder,
              itemSelection.ids.filter((id) => !ids.includes(id)),
            ),
      );
    },
    onOpenGroupMenu: (x: number, y: number) =>
      state.openMenu({ kind: 'group' as const, targetId: group.id, x, y }),
    onRenameColumn: (columnId: ColumnId, title: string) => void state.renameColumn(columnId, title),
    onResizeColumn: (columnId: ColumnId, width: number) => void state.resizeColumn(columnId, width),
    onOpenColumnMenu: (columnId: ColumnId, x: number, y: number) =>
      state.openMenu({ kind: 'column' as const, targetId: columnId, x, y }),
    onMoveColumn: (columnId: ColumnId, before: ColumnId | null) => void state.moveColumn(columnId, before),
    /*
     * **P-292.** monday's header widget owns the whole sort: a click replaces whatever was
     * there with this one column (the toolbar badge goes to `Sort / 1`), and its ✕ drops this
     * column's rule and leaves any other the pane may have added.
     */
    onSortColumn: (columnId: ColumnId, direction: 'asc' | 'desc' | null) =>
      void state.setEffective({
        sort:
          direction === null
            ? effective.sort.filter((rule) => (rule.columnId as string) !== (columnId as string))
            : [{ columnId, direction }],
      }),
    onAddColumn: () => state.openPane('add-column'),
    onDropGroup: (movedGroupId: GroupId, edge: 'above' | 'below') =>
      onDropGroup(movedGroupId, group.id, edge),
    /** P-030: the collapsed strip's summary cells open the footer's menu. */
    onOpenSummaryMenu: (columnId: ColumnId, x: number, y: number) =>
      state.openMenu({ kind: 'column-summary' as const, targetId: columnId, x, y }),
  });

  return (
    /*
     * `data-narrow` is §8.13's *grid* width, published to CSS (round 8f). `stickyWidth` above
     * already drops the checkbox gutter and the conversation cell below `NARROW_GRID_WIDTH`,
     * but the two boxes are still in the row, and the rule that hid them lived in a viewport
     * media query — true on a phone and false for the other way a board becomes narrow: the
     * activity log reserving its 700 px of a 1440 px window (P-016). The row then laid out
     * 6 + 33 + name + 65 inside a sticky box of 6 + name and every data cell was covered by
     * the overflow. One measurement, read by the layout and by the stylesheet.
     */
    <div className="ez-grid" data-testid="board-grid" data-narrow={narrow || undefined}>
      <div
        className="ez-grid__scroller"
        /* P-108: the grid's own keyboard and paste surface, so the inventory can count it. */
        data-testid="grid-scroller"
        ref={scrollerRef}
        onScroll={onScroll}
        role="grid"
        aria-label={payload.board.name}
        aria-rowcount={payload.totals.filtered}
        aria-colcount={grid.visible.length}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      >
        <div
          className="ez-grid__canvas"
          role="rowgroup"
          style={{ height: grid.height, width: stickyWidth + grid.scrollWidth }}
          onDragOver={onCanvasDragOver}
          onDrop={onCanvasDrop}
          onDragEnter={() => store.getState().setInteracting(true)}
          onDragLeave={(event) => {
            // Leaving a child for another child bubbles here too; only a real exit clears.
            if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
            setRowDrop(null);
          }}
          onDragEnd={() => {
            setRowDrop(null);
            store.getState().setInteracting(false);
          }}
        >
          {virtualizer.getVirtualItems().map((virtual) => {
            const row = grid.rows[virtual.index];
            if (!row) return null;
            const group = payload.groups.find((entry) => entry.id === row.groupId);
            if (!group) return null;

            if (row.kind === 'group-header') {
              return (
                <GroupHeader
                  key={`h-${group.id}`}
                  {...headerProps(group, false)}
                  top={virtual.start}
                  height={virtual.size}
                  dropEdge={rowDrop?.rowKey === `group:${group.id}`}
                />
              );
            }
            if (row.kind === 'add-item') {
              return (
                <AddItemRow
                  key={`a-${group.id}`}
                  groupId={group.id}
                  groupColor={group.color}
                  top={virtual.start}
                  height={virtual.size}
                  nameWidth={nameWidth}
                  stickyWidth={stickyWidth}
                  scrollWidth={grid.scrollWidth}
                  terminology={payload.board.itemTerminology}
                  dropEdge={rowDrop?.rowKey === `end:${group.id}`}
                  onAdd={(name) => state.addItem(group.id, name)}
                  /* P-291: a pasted list of rows becomes one item per line. */
                  onAddMany={async (names) => {
                    await state.addItems(group.id, names);
                  }}
                />
              );
            }
            if (row.kind === 'subitems-header') {
              const parentId = String(row.parentItemId);
              return (
                <SubitemsHeaderRow
                  key={`sh-${parentId}`}
                  parentItemId={parentId}
                  groupColor={group.color}
                  top={virtual.start + (row.padTop ?? 0)}
                  height={row.height}
                  gridWidth={stickyWidth + grid.scrollWidth}
                  columns={grid.subitemColumns}
                  nameTitle={
                    grid.subitemColumns.find((column) => column.id === 'name')?.title ?? 'Subitem'
                  }
                  canEditStructure={payload.permissions.canEditStructure}
                  /**
                   * **P-111.** The `+` opens the board's own add-column gallery against the
                   * **subitems board** `[uis:02-row-expanded]`. The pane, the gallery and
                   * `columns.create` were board-agnostic already; only the pane's board was
                   * implicit, so it is named. Until the first subitem exists there is no
                   * subitems board to add to, and the affordance says so rather than opening
                   * a gallery that would write to the parent board.
                   *
                   * **P-326.** The gallery opens **under this `+`** `[uis:20-nested-add-column]`.
                   * It used to anchor to the board header's `+`, so the subitems board's gallery
                   * opened on the parent's header and read as "add a column to the parent".
                   */
                  onAddColumn={(x, y) => {
                    const target = store.getState().subitemsBoardId;
                    if (!target) {
                      state.showToast({
                        message: 'Add a subitem first — the subitems board does not exist yet',
                        kind: 'info',
                      });
                      return;
                    }
                    state.openPane('add-column', target, { x, y });
                  }}
                  onOpenColumnMenu={(columnId, x, y) =>
                    state.openMenu({ kind: 'column', targetId: columnId, x, y })
                  }
                />
              );
            }
            if (row.kind === 'subitem') {
              const parentId = String(row.parentItemId);
              const subitem = (grid.subitemsByParent[parentId] ?? []).find(
                (candidate) => candidate.id === row.itemId,
              );
              if (!subitem) return null;
              return (
                <SubitemRow
                  key={`s-${subitem.id}`}
                  parentItemId={parentId}
                  subitem={subitem}
                  rowIndex={virtual.index}
                  groupColor={group.color}
                  top={virtual.start}
                  height={row.height}
                  gridWidth={stickyWidth + grid.scrollWidth}
                  columns={grid.subitemColumns}
                  context={subitemContext}
                  editing={editing}
                  readOnlyColumnIds={readOnlyColumnIds}
                  onFocusCell={onFocusCell}
                  onStartEditing={(address) => state.startEditing(address)}
                  onCommit={(address, value) => void state.commitCell(address, value)}
                  onCancelEditing={cancelEditing}
                  onNavigate={() => undefined}
                  /**
                   * **P-304.** The menu a nested row opens is the **subitem** menu, so it says
                   * whose subitem this is: the board the delete, archive and duplicate write to
                   * is the subitems board, and the nested table to repaint is this parent's.
                   */
                  onOpenMenu={(x, y, context) =>
                    state.openMenu({
                      kind: 'item',
                      targetId: subitem.id,
                      x,
                      y,
                      context: context === true,
                      parentItemId: parentId,
                    })
                  }
                  onOpenSubitem={() => onOpenItem(subitem.id)}
                  /** P-320: beside the name is the conversation, on a subitem row too. */
                  onOpenSubitemUpdates={() => onOpenItem(subitem.id, { tab: 'updates' })}
                />
              );
            }
            /**
             * **P-308.** The parent is collapsed and the board is on screen; this line is all
             * that is left of the refused read, carrying the api's sentence on hover.
             */
            if (row.kind === 'subitems-unavailable') {
              const parentId = String(row.parentItemId);
              return (
                <SubitemsUnavailableRow
                  key={`su-${parentId}`}
                  parentItemId={parentId}
                  groupColor={group.color}
                  top={virtual.start}
                  height={row.height}
                  gridWidth={stickyWidth + grid.scrollWidth}
                  reason={grid.subitemsError ?? 'Subitems could not be loaded'}
                />
              );
            }
            if (row.kind === 'add-subitem') {
              const parentId = String(row.parentItemId);
              return (
                <AddSubitemRow
                  key={`sa-${parentId}`}
                  parentItemId={parentId}
                  groupColor={group.color}
                  top={virtual.start}
                  height={row.height}
                  gridWidth={stickyWidth + grid.scrollWidth}
                  onAdd={(name) => void state.addSubitem(parentId as never, name)}
                />
              );
            }
            if (row.kind === 'group-footer') {
              return (
                <GroupFooter
                  key={`f-${group.id}`}
                  group={group}
                  top={virtual.start}
                  height={virtual.size}
                  pinned={grid.pinned}
                  scrolling={grid.scrolling}
                  layout={grid.layout}
                  columnRange={columnRange}
                  nameWidth={nameWidth}
                  stickyWidth={stickyWidth}
                  scrollWidth={grid.scrollWidth}
                  onOpenSummaryMenu={(columnId, x, y) =>
                    state.openMenu({ kind: 'column-summary', targetId: columnId, x, y })
                  }
                />
              );
            }

            const item = grid.itemsById.get(row.itemId!);
            if (!item) return null;
            return (
              <GridRow
                key={item.id}
                item={item}
                rowIndex={virtual.index}
                groupColor={group.color}
                groupTitle={group.title}
                top={virtual.start}
                height={virtual.size}
                pinned={grid.pinned}
                scrolling={grid.scrolling}
                layout={grid.layout}
                columnRange={columnRange}
                nameWidth={nameWidth}
                stickyWidth={stickyWidth}
                scrollWidth={grid.scrollWidth}
                context={context}
                selection={selection}
                editing={editing}
                readOnlyColumnIds={readOnlyColumnIds}
                wrappedColumnIds={wrappedColumnIds}
                checked={itemSelection.ids.includes(item.id)}
                rowDragEnabled={rowDragEnabled}
                dropEdge={rowDrop?.rowKey === `item:${item.id}` ? rowDrop.edge : undefined}
                subitemsExpanded={expandedItemIds.includes(item.id)}
                onToggleSubitems={() => void state.toggleSubitems(item.id)}
                onToggleChecked={(event) => toggleChecked(item.id, event)}
                onFocusCell={onFocusCell}
                onStartEditing={(address) => state.startEditing(address)}
                onCommit={(address, value) => void state.commitCell(address, value)}
                onCancelEditing={cancelEditing}
                onNavigate={(direction) => {
                  const geo = grid.geometry;
                  const from = store.getState().selection.focus;
                  if (!from) return;
                  const next =
                    direction === 'next' || direction === 'prev'
                      ? tabTo(geo, from, direction === 'prev')
                      : moveFocus(geo, store.getState().selection, direction).focus;
                  if (next) {
                    store.getState().setSelection(focusCell(geo, next));
                    scrollIntoView(next);
                  }
                }}
                onOpenMenu={(x, y, context) =>
                  state.openMenu({ kind: 'item', targetId: item.id, x, y, context: context === true })
                }
                onOpenItem={() => onOpenItem(item.id)}
                /** P-266: the bubble asks for the conversation, so it names the tab. */
                onOpenUpdates={() => onOpenItem(item.id, { tab: 'updates' })}
                canAddSubitem={canAddSubitem}
                onAddSubitem={() => void state.addSubitemInPlace(item.id)}
                onDragPayload={dragPayload}
              />
            );
          })}
        </div>
      </div>

      {floatingGroup ? (
        <div ref={floatingRef} className="ez-grid__floating">
          <GroupHeader
            {...headerProps(floatingGroup, true)}
            top={0}
            height={floatingGroup.collapsed ? GROUP_COLLAPSED_STRIP_HEIGHT : GROUP_HEADER_HEIGHT}
          />
        </div>
      ) : null}

      {/* P-106: the keyboard reorder says where the row is, since it cannot be seen moving. */}
      <div className="ez-grid__announce" role="status" aria-live="polite" data-testid="grid-announce">
        {announcement}
      </div>

      {payload.totals.filtered === 0 ? (
        <div className="ez-grid__empty" role="status" data-testid="grid-empty">
          <p>{`Showing 0 of ${payload.totals.items} ${payload.board.itemTerminologyPlural}`}</p>
          <button type="button" onClick={() => store.getState().setEffective({ filter: null, search: '' })}>
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  );
}

function collectFilterColumns(filter: FilterGroup | null): string[] {
  if (!filter) return [];
  return [
    ...filter.rules.map((rule) => rule.columnId as string),
    ...filter.groups.flatMap(collectFilterColumns),
  ];
}

export { EMPTY_ITEM_SELECTION };
